/* eslint-disable import/no-cycle */
import ApiService from '@/services/api.service';
import StorageService from '@/services/storage.service';
import {
  AuthError, ForbiddenError, HttpError, PMSError, TooManyAttemptsError, ValidationError,
} from '@/errors';
import { apiEndpoint, appEndpoint, supertokensEndpoint } from '@/shared';
import MatomoService from '@/services/matomo.service';
import {
  signIn, signOut, signUp, submitNewPassword,
} from 'supertokens-web-js/recipe/emailpassword';
import { EmailVerificationClaim } from 'supertokens-web-js/recipe/emailverification';
import Session from 'supertokens-web-js/recipe/session';

function handleApiError(error) {
  const { status, data } = error.response;
  const rlr = error.response.headers['x-ratelimit-reset'] || 0;
  switch (status) {
    case 400: // BadRequest
      return new HttpError(status, data.message);
    case 401:
      // TODO: remove user from storage?
      return new AuthError(status, data.message);
    case 409: // PMSError
      return new PMSError(status, data.message);
    case 403:
      return new ForbiddenError(status, data.message);
    case 422:
      return new ValidationError(status, data.message, data.errors);
    case 429:
      return new TooManyAttemptsError(rlr ? data.message : data.errors.email[0], rlr);
    default:
      return error;
  }
}

const UserService = {
  async login(payload) {
    const { uuid } = payload;
    if (uuid) {
      const response = await fetch(`${supertokensEndpoint}/login-with-otl`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.status === 200) return this.getUser();
      return null;
    }
    const response = await signIn({
      formFields: [
        {
          id: 'email',
          value: payload.email,
        },
        {
          id: 'password',
          value: payload.password,
        },
      ],
    });
    switch (response.status) {
      case 'FIELD_ERROR':
        throw handleApiError({
          response: {
            status: 400,
            data: {
              message: 'Authentication filed.',
            },
            headers: {
              'x-ratelimit-reset': 0,
            },
          },
        });
      case 'WRONG_CREDENTIALS_ERROR':
        throw handleApiError({
          response: {
            status: 422,
            data: {
              message: 'You have entered an wrong password.',
              errors: {
                password: ['You have entered an wrong password.'],
              },
            },
            headers: {
              'x-ratelimit-reset': 0,
            },
          },
        });
      case 'SIGN_IN_NOT_ALLOWED':
        throw handleApiError({
          response: {
            status: 422,
            data: {
              message: 'Email does not exist.',
              errors: {
                email: ['Email does not exist.'],
              },
            },
            headers: {
              'x-ratelimit-reset': 0,
            },
          },
        });
      default:
    }
    const user = await this.getUser();
    return user;
  },

  async directLogin(payload) {
    try {
      // now we can make login request
      const response = await fetch(`${supertokensEndpoint}/login-with-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const responseContext = await response.json();
      if (responseContext.status !== 'OK') {
        throw handleApiError({
          response: {
            status: 400,
            data: {
              message: 'Cannot sign in with token',
            },
            headers: {
              'x-ratelimit-reset': 0,
            },
          },
        });
      }
      return await this.getUser();
    } catch (error) {
      throw handleApiError({
        response: {
          status: 400,
          data: {
            message: 'Cannot sign in with token',
          },
          headers: {
            'x-ratelimit-reset': 0,
          },
        },
      });
    }
  },

  async logout() {
    await signOut();
    // Deleting all cookies
    const cookies = document.cookie.split(';');
    cookies.forEach((cookie) => {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    });

    MatomoService.deleteUser();
    StorageService.removeUser();
    StorageService.removeProperties();
    ApiService.setProperty();
    // ApiService.removeHeader();
    ApiService.disableAuthErrorInterceptor();
  },

  async getUser(masterdata = false) {
    try {
      if (!(await Session.doesSessionExist())) {
        return null;
      }

      const response = await ApiService.get(`${apiEndpoint}/user${masterdata ? '/masterdata' : ''}`);
      const user = response.data;

      if (typeof user === 'object' && user.id != null) {
        MatomoService.setUser(user);
        StorageService.setUser(user);
        return user;
      }
      MatomoService.deleteUser();
      StorageService.removeUser();
      ApiService.setProperty();
      return null;
    } catch (error) {
      await signOut();
      MatomoService.deleteUser();
      StorageService.removeUser();
      ApiService.setProperty();
      throw new AuthError(error.response.status, error.response.data.message);
    }
  },

  async getInfo() {
    try {
      const { data: info } = await ApiService.get(`${apiEndpoint}/info`);
      return info;
    } catch (error) {
      return null;
    }
  },
  async resendEmail() {
    try {
      await ApiService.post(`${appEndpoint}/email/verify`);
      return true;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async updateProfile(data) {
    try {
      const url = `${apiEndpoint}/user/profile`;
      const response = await ApiService.post(url, data);
      const user = response.data;
      StorageService.setUser(user);
      return user;
    } catch (error) {
      throw handleApiError(error);
    }
  },
  async updateProfileData(data) {
    try {
      const url = `${apiEndpoint}/user`;
      const response = await ApiService.put(url, data);
      const user = response.data;
      StorageService.setUser(user);
      return user;
    } catch (error) {
      throw handleApiError(error);
    }
  },
  async sendResetPasswordEmail(email) {
    try {
      const url = `${appEndpoint}/password/email`;
      await ApiService.post(url, { email });
    } catch (error) {
      throw handleApiError(error);
    }
    return true;
  },

  async sendChangeEmail(email) {
    try {
      const url = `${appEndpoint}/email/send-email`;
      await ApiService.post(url, { email });
    } catch (error) {
      throw handleApiError(error);
    }
    return true;
  },
  async sendRenewMagicLink() {
    try {
      await fetch(`${supertokensEndpoint}/renew-magic-link`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return await this.getUser();
    } catch (error) {
      throw handleApiError(error);
    }
  },
  async getNewMagicLink() {
    try {
      const response = await fetch(`${supertokensEndpoint}/renew-magic-link`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const responseData = await response.json();
      return responseData.magicLink;
    } catch (error) {
      throw handleApiError(error);
    }
  },
  async changeEmail(email, token, newEmail) {
    return fetch(`${supertokensEndpoint}/change-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, token, newEmail }),
    });
  },

  async resetPassword(token, password, email) {
    try {
      await submitNewPassword({
        method: 'token',
        formFields: [
          {
            id: 'password',
            value: password,
          },
        ],
        token,
        userContext: {
          email,
        },
        options: {
          preAPIHook: async (context) => {
            const { url } = context;
            const { requestInit } = context;
            const userEmail = context.userContext?.email;
            const body = JSON.parse(requestInit.body);
            requestInit.body = JSON.stringify({
              ...body,
              email: userEmail,
            });
            return {
              requestInit,
              url,
            };
          },
        },
      });
    } catch (error) {
      switch (error.message) {
        case 'TOKEN_EMPTY':
          throw handleApiError({
            response: {
              status: 400,
              data: {
                message: 'Token is empty.',
              },
              headers: {
                'x-ratelimit-reset': 0,
              },
            },
          });
        case 'PASSWORD_SAME_AS_OLD':
          throw handleApiError({
            response: {
              status: 422,
              data: {
                message: 'New password cannot be the same as your old password.',
                errors: {
                  password: ['New password cannot be the same as your old password.'],
                },
              },
              headers: {
                'x-ratelimit-reset': 0,
              },
            },
          });
        case 'USER_NOT_EXISTS':
          throw handleApiError({
            response: {
              status: 400,
              data: {
                message: 'User does not exist.',
              },
              headers: {
                'x-ratelimit-reset': 0,
              },
            },
          });
        case 'INVALID_PARAMS':
        case 'INVALID_TOKEN':
          throw handleApiError({
            response: {
              status: 422,
              data: {
                message: 'This password reset token is invalid or expired.',
                errors: {
                  email: ['This password reset token is invalid or expired.'],
                },
              },
              headers: {
                'x-ratelimit-reset': 0,
              },
            },
          });
        default:
          throw handleApiError({
            response: {
              status: 400,
              data: {
                message: 'Authentication Failed.',
              },
              headers: {
                'x-ratelimit-reset': 0,
              },
            },
          });
      }
    }

    return true;
  },

  // eslint-disable-next-line camelcase, no-unused-vars
  async register(email, password, tos_agreed) {
    const response = await signUp({
      formFields: [
        {
          id: 'email',
          value: email,
        },
        {
          id: 'password',
          value: password,
        },
      ],
    });

    switch (response.status) {
      case 'OK':
        break;
      case 'SIGN_UP_NOT_ALLOWED':
        throw handleApiError({
          response: {
            status: 400,
            data: {
              message: response.reason,
            },
            headers: {
              'x-ratelimit-reset': 0,
            },
          },
        });
      case 'FIELD_ERROR':
        throw handleApiError({
          response: {
            status: 422,
            data: {
              message: 'Cannot sign up as email already exists.',
              errors: {
                email: ['Cannot sign up as email already exists.'],
              },
            },
            headers: {
              'x-ratelimit-reset': 0,
            },
          },
        });
      default:
        throw handleApiError({
          response: {
            status: 400,
            data: {
              message: 'Registration failed.',
            },
            headers: {
              'x-ratelimit-reset': 0,
            },
          },
        });
    }
  },

  async setupStep(payload) {
    const url = `${apiEndpoint}/user/setup`;
    const { data } = await ApiService.patch(url, payload);
    Session.attemptRefreshingSession();
    return data;
  },

  async getPropertGroup() {
    try {
      const url = `${apiEndpoint}/property-group-info`;
      const { data } = await ApiService.get(url);
      return data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async isEmailVerified() {
    if (await Session.doesSessionExist()) {
      const validationErrors = await Session.validateClaims();
      if (validationErrors.length !== 0) {
        // eslint-disable-next-line no-restricted-syntax
        for (const err of validationErrors) {
          if (err.validatorId === EmailVerificationClaim.id) {
            return false;
          }
        }
      }
      return true;
    }
    return false;
  },

  async checkEmailAddress(payload) {
    try {
      const url = `${appEndpoint}/email/check`;
      const { data } = await ApiService.post(url, payload);
      return data;
    } catch (error) {
      throw handleApiError(error);
    }
  },
};

export default UserService;
